import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Users, Star, Search, Loader2, Tag } from "lucide-react";
import { Footer } from "@/components/Footer";
import { fetchAllCoursesWithPromotions, fetchCategories } from "@/services/courseService";
import { SkeletonCourseGrid } from "@/components/ui/skeleton-course-card";
import { usePerformanceMonitor, usePageLoadPerformance } from "@/hooks/usePerformanceMonitor";
import { useOptimizedQuery } from "@/hooks/useOptimizedQuery";

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  instructor_name: string;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  syllabus?: any;
  is_published?: boolean;
  is_coming_soon?: boolean;
  discount_percentage?: number;
  activePromotion?: {
    name: string;
    discount_percentage: number;
    end_date: string;
  };
  discounted_price?: number;
}

export default function Courses() {
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("popular");
  const { measureAsync } = usePerformanceMonitor("Courses");
  usePageLoadPerformance("Courses");
  const { t } = useTranslation();

  // Use optimized queries with caching
  const { data: courses = [], isLoading: coursesLoading } = useOptimizedQuery({
    queryKey: ['courses-with-promotions'],
    queryFn: fetchAllCoursesWithPromotions,
    dataType: 'static',
  });

  const { data: categories = [], isLoading: categoriesLoading } = useOptimizedQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    dataType: 'static',
  });

  const isLoading = coursesLoading || categoriesLoading;

  // Filter and sort courses whenever data or filters change
  useEffect(() => {
    if (courses.length > 0) {
      filterAndSortCourses();
    }
  }, [courses, searchTerm, categoryFilter, levelFilter, sortBy]);

  const filterAndSortCourses = () => {
    let filtered = [...courses];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (course) =>
          course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.instructor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((course) => course.category === categoryFilter);
    }

    // Level filter
    if (levelFilter !== "all") {
      filtered = filtered.filter((course) => course.level === levelFilter);
    }

    // Sorting
    switch (sortBy) {
      case "popular":
        filtered.sort((a, b) => b.students_count - a.students_count);
        break;
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        break;
    }

    // Calculate discounted prices for courses with promotions
    const processedCourses = filtered.map(course => {
      if (course.discount_percentage && course.discount_percentage > 0) {
        const discountedPrice = course.price * (1 - course.discount_percentage / 100);
        return {
          ...course,
          discounted_price: discountedPrice,
          activePromotion: {
            name: 'Active Promotion',
            discount_percentage: course.discount_percentage,
            end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          }
        };
      }
      return course;
    });

    setFilteredCourses(processedCourses);
  };

  const levels = Array.from(new Set(courses.map((c) => c.level)));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto page-container">
          <div className="mb-12 mt-20 text-center">
            <div className="h-12 w-96 bg-muted animate-pulse rounded mx-auto mb-4" />
            <div className="h-6 w-64 bg-muted animate-pulse rounded mx-auto" />
          </div>
          <div className="mb-8 space-y-4">
            <div className="h-12 max-w-2xl bg-muted animate-pulse rounded mx-auto" />
          </div>
          <SkeletonCourseGrid count={9} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="container mx-auto page-container">
        {/* Header */}
        <div className="mb-12 mt-20 text-center">
          <h1 className="heading-page mb-4">
            <span className="bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
              {t("courses.title")}
            </span>{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {t("courses.subtitle")}
            </span>
          </h1>
          <p className="text-xl text-muted-foreground">
            {t("courses.description")}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("courses.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-card border-border"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4 items-center justify-center">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px] bg-card">
                <SelectValue placeholder={t("courses.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("courses.allCategories")}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[200px] bg-card">
                <SelectValue placeholder={t("courses.allLevels")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("courses.allLevels")}</SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] bg-card">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">{t("courses.mostPopular")}</SelectItem>
                <SelectItem value="rating">{t("courses.highestRated")}</SelectItem>
                <SelectItem value="price-low">{t("courses.priceLowToHigh")}</SelectItem>
                <SelectItem value="price-high">{t("courses.priceHighToLow")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-center text-muted-foreground">
          {t("courses.showing")} {filteredCourses.length} {filteredCourses.length === 1 ? t("courses.course") : t("courses.courses")}
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-muted-foreground">{t("courses.noCourses")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course) => (
              <Card
                key={course.id}
                className="card-interactive overflow-hidden"
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between flex-wrap gap-2">
                    <Badge variant="secondary" className="backdrop-blur-md">{course.category}</Badge>
                    <Badge variant="outline" className="backdrop-blur-md">{course.level}</Badge>
                    {course.syllabus?.status === 'coming_soon' && (
                      <Badge variant="outline" className="backdrop-blur-md bg-blue-500/10 text-blue-600 border-blue-200">
                        {t("courses.comingSoon")}
                      </Badge>
                    )}
                    {course.activePromotion && (
                      <Badge variant="default" className="backdrop-blur-md gap-1">
                        <Tag className="w-3 h-3" />
                        {course.activePromotion.discount_percentage}% OFF
                      </Badge>
                    )}
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="text-2xl group-hover:text-primary transition-colors">
                    {course.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>{course.students_count}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 fill-primary text-primary" />
                      <span>{course.rating}</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      {t("courses.instructor")}: {course.instructor_name}
                    </p>
                  </div>
                </CardContent>
                <CardFooter className="flex items-center justify-between">
                  <div>
                    {course.syllabus?.status === 'coming_soon' ? (
                      <span className="text-lg font-semibold text-muted-foreground">{t("courses.launchingSoon")}</span>
                    ) : course.discounted_price ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-primary">
                            ${course.discounted_price.toFixed(2)}
                          </span>
                          <span className="text-lg text-muted-foreground line-through">
                            ${course.price}
                          </span>
                        </div>
                        <p className="text-sm text-green-600 font-medium mt-1">
                          {t("courses.save")} ${(course.price - course.discounted_price).toFixed(2)}!
                        </p>
                      </>
                    ) : (
                      <span className="text-2xl font-bold text-primary">${course.price}</span>
                    )}
                  </div>
                  <Button 
                    variant={course.syllabus?.status === 'coming_soon' ? "outline" : "default"}
                    disabled={course.syllabus?.status === 'coming_soon'}
                    className="group-hover:shadow-[var(--glow-primary)]"
                    asChild={course.syllabus?.status !== 'coming_soon'}
                  >
                    {course.syllabus?.status === 'coming_soon' ? (
                      <span>{t("courses.comingSoon")}</span>
                    ) : (
                      <Link to={`/courses/${course.slug || course.id}`}>{t("courses.viewDetails")}</Link>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
